import { saveAs } from "file-saver";
import JSZip from "jszip";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useQuery, useZero } from "@/composables/useZero";
import type { DossierDocument } from "@/composables/useLoanApplicationSortedDocuments";
import type { FileClickMode } from "@/enums/FileClickMode";
import { nameFromLocalPath } from "@/helpers/fileHelpers";
import { api } from "@/loanLinkApi";
import type { FileMetadata } from "@/models/FileMetadata";
import { useDossierDocumentUrlStore } from "./useDossierDocumentUrlStore";
import { useDossierStore } from "./useDossierStore";
import { DateTime } from "luxon";
import { upload } from "@/helpers/networkHelpers";
import { useGlobalUploadStore } from "./useGlobalUploadStore";
import { mutators, queries } from "@loanlink-nl/zero";

async function callWithBackoff<T>(
	fn: () => Promise<T>,
	maxRetries: number,
	delay: number,
	retries = 0,
): Promise<T> {
	return fn().catch(async (err) => {
		if (retries >= maxRetries) {
			throw err;
		}
		await new Promise((resolve) => setTimeout(resolve, delay));
		return callWithBackoff(fn, maxRetries, delay, retries + 1);
	});
}

export const useFileUploadStore = defineStore("file-upload", () => {
	const dossierStore = useDossierStore();

	const filesToDocumentId = ref<{ file: File; documentId: string }[]>([]);

	const z = useZero();
	const { data: existingFiles } = useQuery(() =>
		queries.dossierDocuments.all({
			dossierId: dossierStore.selectedDossier?.id ?? "",
		}),
	);

	const existingFilesOrdered = computed(() => {
		return existingFiles.value
			?.filter((doc) => doc.uploadedAt !== null)
			.sort((a, b) => a.filename.localeCompare(b.filename));
	});

	const filesToUpload = ref<FileMetadata[]>([]);
	const selectedFromExistingFiles = ref<DossierDocument[]>([]);
	const lastSelectedFile = ref<DossierDocument | undefined>(undefined);

	const anyFilesSelected = computed(() => {
		return selectedFromExistingFiles.value.length > 0;
	});

	function $reset() {
		filesToUpload.value = [];
		selectedFromExistingFiles.value = [];
		lastSelectedFile.value = undefined;
	}

	function getIndexOfFileToUpload(file: File) {
		return filesToUpload.value.findIndex((f) => f.file === file);
	}

	function selectFile(file: DossierDocument, mode: FileClickMode) {
		if (mode === "replace") {
			selectedFromExistingFiles.value = [file];
			lastSelectedFile.value = file;
		} else if (mode === "add") {
			const indexOfSelectedFile = selectedFromExistingFiles.value.findIndex(
				(d) => d.id === file.id,
			);
			const selectedFileAlreadySelected = indexOfSelectedFile > -1;

			if (selectedFileAlreadySelected) {
				selectedFromExistingFiles.value.splice(indexOfSelectedFile, 1);
			} else {
				selectedFromExistingFiles.value.push(file);
			}
			lastSelectedFile.value = file;
		} else if (mode === "between") {
			const list = existingFiles.value ?? [];
			const lastSelectedIndex = lastSelectedFile.value
				? list.findIndex((d) => d.id === lastSelectedFile.value?.id)
				: -1;
			if (lastSelectedIndex === -1) {
				selectedFromExistingFiles.value = [file];
				lastSelectedFile.value = file;
				return;
			}
			const currentSelectedIndex = list.findIndex((d) => d.id === file.id);
			if (currentSelectedIndex === -1) {
				selectedFromExistingFiles.value = [file];
				lastSelectedFile.value = file;
				return;
			}

			const lowestIndex = Math.min(lastSelectedIndex, currentSelectedIndex);
			const highestIndex = Math.max(lastSelectedIndex, currentSelectedIndex);

			selectedFromExistingFiles.value = list.slice(
				lowestIndex,
				highestIndex + 1,
			);
		}
	}

	function addToFilesToUpload(file: File) {
		if (getIndexOfFileToUpload(file) === -1) {
			const filename = nameFromLocalPath(file.name);
			filesToUpload.value.push({ file: file, newName: filename });
		}
	}

	function addFilesToFilesToUpload(files: Iterable<File>) {
		for (const file of files) {
			addToFilesToUpload(file);
		}
	}

	function clearFilesToUpload() {
		filesToUpload.value = [];
	}

	function updateNameOfFileToUpload(
		fileMetadata: FileMetadata,
		newValue: string,
	) {
		fileMetadata.newName = newValue;
	}

	function removeFileToUpload(fileMetadata: FileMetadata) {
		const indexOfFile = filesToUpload.value.indexOf(fileMetadata);
		if (indexOfFile !== -1) {
			filesToUpload.value.splice(indexOfFile, 1);
		}
	}

	async function uploadFiles() {
		await Promise.all(
			filesToUpload.value.map(async (file) => {
				await uploadFile(file);
			}),
		);

		clearFilesToUpload();
	}

	async function uploadFile(file: FileMetadata) {
		const dossierStore = useDossierStore();
		const fileExtension = file.file.name.slice(file.file.name.lastIndexOf("."));
		const filename = `${file.newName}${fileExtension}`;

		if (!dossierStore.selectedDossier?.id) {
			throw new Error("No dossier selected");
		}

		const documentId = crypto.randomUUID();
		const dossierId = dossierStore.selectedDossier.id;
		await z.value.mutate(
			mutators.dossierDocument.create({
				id: documentId,
				dossierId,
				filename,
				contentType: file.file.type,
				byteSize: file.file.size,
			}),
		).server;

		filesToDocumentId.value.push({ file: file.file, documentId });

		const globalUploadStore = useGlobalUploadStore();
		globalUploadStore.setUploadState(documentId, {
			state: "uploading",
			progress: 0,
			filename,
		});

		try {
			const {
				data: { uploadUrl },
			} = await callWithBackoff(
				() => api.dossier.getDocumentUploadUrl(dossierId, documentId),
				5,
				1000,
			);

			const uploadResponse = await upload(uploadUrl, {
				method: "PUT",
				body: file.file,
				headers: {
					"x-ms-blob-type": "BlockBlob",
					"Content-Type": file.file.type,
				},
				onProgress: (progress) => {
					globalUploadStore.updateUploadProgress(documentId, progress);
				},
			});

			if (!uploadResponse) {
				throw new Error("Failed to upload dossier document");
			}

			z.value.mutate(
				mutators.dossierDocument.updateUploadedAt({
					id: documentId,
					uploadedAt: DateTime.now().toMillis(),
				}),
			);

			globalUploadStore.completeUpload(documentId);
		} catch (err) {
			globalUploadStore.setUploadError(
				documentId,
				err instanceof Error ? err.message : "Upload failed",
			);
			throw err;
		}

		return documentId;
	}

	async function downloadFiles() {
		const dossierStore = useDossierStore();

		if (!dossierStore.selectedDossier) {
			throw new Error("No dossier selected");
		}

		const filesToDownload = anyFilesSelected.value
			? selectedFromExistingFiles.value
			: existingFiles.value;

		let blobToSave: Blob | undefined;
		let savedFileName: string | undefined;

		const dossierDocumentTokenStore = useDossierDocumentUrlStore();

		if (filesToDownload.length === 1) {
			const [file] = filesToDownload;
			if (!file) {
				throw new Error("File is not available");
			}

			const fileUrl = await dossierDocumentTokenStore.getFileUrl(file.id);
			const fetchedFile = await fetch(fileUrl);
			blobToSave = await fetchedFile.blob();
			savedFileName = file.filename;
		} else if (filesToDownload.length >= 2) {
			const zip = new JSZip();

			const fetchedFiles = await Promise.all(
				filesToDownload.map(async (file) => {
					const fileUrl = await dossierDocumentTokenStore.getFileUrl(file.id);
					const fetchedFile = await fetch(fileUrl);
					const fileData = await fetchedFile.blob();

					return [file, fileData] as const;
				}),
			);

			for (const [file, fileData] of fetchedFiles) {
				zip.file(file.filename, fileData, {
					date: file.creationDate ? new Date(file.creationDate) : undefined,
				});
			}

			blobToSave = await zip.generateAsync({ type: "blob" });

			const currentDossierName = dossierStore.selectedDossier.name;
			savedFileName = `${currentDossierName ?? "Dossier"} - documenten.zip`;
		}

		if (blobToSave !== undefined) {
			saveAs(blobToSave, savedFileName ?? "undefined");
		}
	}

	return {
		filesToUpload,
		existingFiles: existingFilesOrdered,
		selectedFromExistingFiles,
		lastSelectedFile,
		filesToDocumentId,

		uploadFiles,
		uploadFile,
		selectFile,
		addFilesToFilesToUpload,
		downloadFiles,
		removeFileToUpload,
		updateNameOfFileToUpload,
		clearFilesToUpload,
		addToFilesToUpload,

		$reset,
	};
});
